import React, { memo, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Select, Section, Text } from 'hoi-poi-ui';
import CreateViewModal from "./components/CreateViewModal";
import DeleteViewModal from "./components/DeleteViewModal";
import { useWeb5Context } from "../../lib/Web5Context";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'Views');
const FilterViews = /*#__PURE__*/memo(_ref => {
  let {
    className,
    labels,
    labelsSave,
    labelsDelete,
    isOpen: isOpenSaveView,
    onToggleSaveViewsModal,
    useViewsInFilters,
    useViewsInMenu,
    views,
    selectedView,
    customView,
    onChange,
    onChangeCustomView,
    onSave,
    onDelete,
    onDeleteCustomView,
    handleOnToggleFilter,
    canManagePublicViews,
    canManagePrivateViews,
    toggled
  } = _ref;
  const classes = useStyles();
  const classNames = classnames(classes.root, className);
  const [isOpenDeleteView, setIsOpenDeleteView] = useState(false);
  const {
    t
  } = useWeb5Context();
  const onToggleDeleteViewModal = useCallback(() => {
    setIsOpenDeleteView(!isOpenDeleteView);
  }, [isOpenDeleteView]);
  const handleOnChange = useCallback(value => {
    if (customView) {
      return onChangeCustomView(value);
    }
    return onChange(value);
  }, [customView, onChangeCustomView, onChange]);
  const handleOnDelete = useCallback(value => {
    if (customView && onDeleteCustomView) {
      return onDeleteCustomView(value);
    }
    return onDelete && onDelete(value);
  }, [customView, onDeleteCustomView, onDelete]);
  const activeFields = useMemo(() => {
    if (!selectedView) return null;
    return 1;
  }, [selectedView]);
  const canDeleteView = useMemo(() => {
    var _selectedView$item;
    if (!selectedView || !canManagePublicViews && !canManagePrivateViews) return false;
    let isPublic = selectedView === null || selectedView === void 0 ? void 0 : (_selectedView$item = selectedView.item) === null || _selectedView$item === void 0 ? void 0 : _selectedView$item.isPublic;
    let finalIsPublic = false;
    if (typeof isPublic !== 'boolean') {
      if (isPublic === 'True') finalIsPublic = true;else if (isPublic === '1') finalIsPublic = true;else finalIsPublic = false;
    }
    if (finalIsPublic && canManagePublicViews) return true;
    if (!finalIsPublic && canManagePrivateViews) return true;
    return false;
  }, [selectedView, canManagePublicViews, canManagePrivateViews]);
  const viewsField = useMemo(() => {
    if (!useViewsInFilters && useViewsInMenu) return null;
    if (!useViewsInMenu && !useViewsInFilters) {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.selectView
      }, /*#__PURE__*/React.createElement(Text, {
        className: classes.selectViewLabel,
        type: "subtitle1"
      }, (labels === null || labels === void 0 ? void 0 : labels.views) || t('label_views')), /*#__PURE__*/React.createElement(Select, {
        placeholder: (labels === null || labels === void 0 ? void 0 : labels.viewsPlaceholder) || t('label_selectone'),
        noOptionsPlaceholder: (labels === null || labels === void 0 ? void 0 : labels.viewsNoOptionsPlaceholder) || t('label_no_options'),
        loadingPlaceholder: (labels === null || labels === void 0 ? void 0 : labels.viewsLoadingPlaceholder) || t('wait_loading'),
        value: selectedView,
        options: views,
        onChange: handleOnChange
      }));
    } else if (useViewsInFilters) {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.sectionView
      }, /*#__PURE__*/React.createElement(Section, {
        title: (labels === null || labels === void 0 ? void 0 : labels.views) || t('label_views'),
        onChange: handleOnToggleFilter({
          filter: 'fm_defaultViewsSection'
        }),
        isOpen: toggled['fm_defaultViewsSection'],
        activeFields: activeFields
      }, /*#__PURE__*/React.createElement(Select, {
        placeholder: (labels === null || labels === void 0 ? void 0 : labels.viewsPlaceholder) || t('label_selectone'),
        noOptionsPlaceholder: (labels === null || labels === void 0 ? void 0 : labels.viewsNoOptionsPlaceholder) || t('label_no_options'),
        loadingPlaceholder: (labels === null || labels === void 0 ? void 0 : labels.viewsLoadingPlaceholder) || t('wait_loading'),
        value: selectedView,
        options: views,
        onChange: handleOnChange,
        isFullWidth: true
      }), canDeleteView && /*#__PURE__*/React.createElement(Text, {
        className: classes.deleteView,
        type: "caption",
        onClick: onToggleDeleteViewModal
      }, (labels === null || labels === void 0 ? void 0 : labels.deleteView) || t('action_delete_views'))));
    }
  }, [classes, labels, useViewsInFilters, useViewsInMenu, handleOnToggleFilter, toggled, onToggleDeleteViewModal, views, selectedView, handleOnChange, activeFields, canDeleteView, t]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames
  }, viewsField, isOpenSaveView && /*#__PURE__*/React.createElement(CreateViewModal, {
    isOpen: isOpenSaveView,
    labels: labelsSave,
    onSave: onSave,
    onClose: onToggleSaveViewsModal,
    canManagePublicViews: canManagePublicViews,
    canManagePrivateViews: canManagePrivateViews
  }), isOpenDeleteView && /*#__PURE__*/React.createElement(DeleteViewModal, {
    isOpen: isOpenDeleteView,
    labels: labelsDelete,
    onDelete: handleOnDelete,
    onClose: onToggleDeleteViewModal,
    selectedView: selectedView
  }));
});
process.env.NODE_ENV !== "production" ? FilterViews.propTypes = {
  className: PropTypes.string,
  views: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  selectedView: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  }),
  canManagePublicViews: PropTypes.bool,
  canManagePrivateViews: PropTypes.bool,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  onToggleViewsModal: PropTypes.bool,
  labels: PropTypes.shape({
    views: PropTypes.string,
    viewsPlaceholder: PropTypes.string,
    viewsNoOptionsPlaceholder: PropTypes.string,
    viewsLoadingPlaceholder: PropTypes.string,
    deleteView: PropTypes.string
  }),
  labelsSave: PropTypes.shape({
    title: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    viewName: PropTypes.string,
    saveAs: PropTypes.string,
    viewNamePlaceholder: PropTypes.string,
    saveAsPlaceholder: PropTypes.string
  }),
  labelsDelete: PropTypes.shape({
    title: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    questionText: PropTypes.string,
    adviceText: PropTypes.string,
    confirmWriteText: PropTypes.string,
    confirmWord: PropTypes.string,
    confirmWritePlaceholder: PropTypes.string
  })
} : void 0;
export default FilterViews;